import React from "react";
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import CreditCard, { Focused } from "react-credit-cards";
import "react-credit-cards/es/styles-compiled.css";

import { maskExpiry } from "../../utils/masks";
import {
  InstallmentsResponse,
  PaymentMethod,
  PaymentSummaryResponse,
} from "../controller";

type PaymentMethodOption = {
  id: PaymentMethod;
  label: string;
  icon: React.ReactNode;
};

interface CreditCardData {
  name: string;
  number: string;
  expiry: string;
  cvc: string;
  issuer: string;
  installments?: number;
}

interface CreditCardErrors {
  number: boolean;
  expiry: boolean;
  cvc: boolean;
  name: boolean;
}

interface PaymentMethodFormProps {
  methods: PaymentMethodOption[];
  selectedMethod?: PaymentMethod | null;
  onSelectMethod: (method: PaymentMethod) => void;
  summaryBanner?: React.ReactNode;

  creditCard: CreditCardData;
  creditCardErrors: CreditCardErrors;
  focusedField?: Focused;
  onCCNameChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onCCNumberChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onCCExpiryChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onCCCVCChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onCCFocusChange: (field: Focused | undefined) => void;
  onIdentifyCreditCard: (data: { issuer: string }, isValid: boolean) => void;

  installments?: InstallmentsResponse;
  installmentsLoading: boolean;
  installmentsError?: string;
  selectedInstallment: number;
  onInstallmentSelection: (event: SelectChangeEvent<number> | any) => void;

  paymentSummary?: PaymentSummaryResponse;
  paymentSummaryError?: string;
  paymentSummaryLoading: boolean;

  cardBrandRequiredMessage: string;
  formatCurrency: (value: number) => string;
}

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  methods,
  selectedMethod,
  onSelectMethod,
  summaryBanner,
  creditCard,
  creditCardErrors,
  focusedField,
  onCCNameChange,
  onCCNumberChange,
  onCCExpiryChange,
  onCCCVCChange,
  onCCFocusChange,
  onIdentifyCreditCard,
  installments,
  installmentsLoading,
  installmentsError,
  selectedInstallment,
  onInstallmentSelection,
  paymentSummary,
  paymentSummaryError,
  paymentSummaryLoading,
  cardBrandRequiredMessage,
  formatCurrency,
}) => {
  const renderMethodCard = (method: PaymentMethodOption, isSelected: boolean) => (
    <Card
      key={method.id}
      onClick={() => onSelectMethod(method.id)}
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        p: 2,
        outline: isSelected ? "1px solid #1EA097" : "1px solid #ccc",
        borderRadius: 2,
        cursor: "pointer",
        boxShadow: "none",
      }}
    >
      <Box
        width={40}
        height={40}
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="1px solid #ccc"
        borderRadius={1}
        mr={2}
        fontSize="1.5rem"
      >
        {method.icon}
      </Box>
      <Typography variant="body1">{method.label}</Typography>
    </Card>
  );

  const renderInstallments = () => {
    if (selectedMethod !== PaymentMethod.CREDIT_CARD) {
      return null;
    }

    const brand = creditCard.issuer;
    const lacksBrand = !brand || brand === "UNKNOWN";
    const combinedError =
      installmentsError || (paymentSummaryLoading ? undefined : paymentSummaryError);
    const currentInstallment = creditCard.installments ?? selectedInstallment;

    return (
      <Box marginTop={1}>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Parcelamento
        </Typography>

        {installmentsLoading && (
          <Box display="flex" alignItems="center" gap={1} marginBottom={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Carregando opções de parcelamento…</Typography>
          </Box>
        )}

        {combinedError && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {combinedError}
          </Alert>
        )}

        {!installmentsLoading && !combinedError && lacksBrand && (
          <Alert severity="info" sx={{ marginBottom: 2 }}>
            {cardBrandRequiredMessage}
          </Alert>
        )}

        {installments && !installmentsLoading && !combinedError && (
          installments.options.length > 0 ? (
            <FormControl fullWidth>
              <InputLabel id="installments-select-label">Parcelas</InputLabel>
              <Select
                labelId="installments-select-label"
                label="Parcelas"
                value={currentInstallment}
                onChange={onInstallmentSelection}
              >
                {installments.options.map((option) => {
                  const disabled =
                    option.n > installments.max_allowed_installments_for_amount;
                  const simulated =
                    installments.simulated &&
                    option.value_split_cents != null &&
                    option.value_total_cents != null;

                  let label = `${option.n} parcela${option.n > 1 ? "s" : ""}`;
                  if (simulated) {
                    const split = formatCurrency(option.value_split_cents!);
                    const total = formatCurrency(option.value_total_cents!);
                    label = `${option.n}x de ${split} (Total ${total})`;
                  }

                  return (
                    <MenuItem key={option.n} value={option.n} disabled={disabled}>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="body1">{label}</Typography>
                        {option.no_interest && (
                          <Typography variant="caption" color="success.main">
                            Sem juros pela política
                          </Typography>
                        )}
                        {disabled && (
                          <Typography variant="caption" color="error.main">
                            Indisponível para este valor
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          ) : (
            <Alert severity="warning" sx={{ marginBottom: 2 }}>
              Parcelamento indisponível para este valor.
            </Alert>
          )
        )}

        {paymentSummaryLoading && (
          <Box display="flex" alignItems="center" gap={1} marginTop={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Validando resumo do pagamento…</Typography>
          </Box>
        )}

        {paymentSummary?.notes && (
          <Alert severity="info" sx={{ marginTop: 2 }}>
            {paymentSummary.notes}
          </Alert>
        )}
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Typography variant="h5" fontWeight={700}>
        Método de pagamento
      </Typography>

      <Box display="grid" gap={2} gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}>
        {methods.map((method) => renderMethodCard(method, selectedMethod === method.id))}
      </Box>

      {summaryBanner}

      {selectedMethod === PaymentMethod.CREDIT_CARD && (
        <>
          <Divider />

          <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
            <Box
              sx={{
                "& .rccs": {
                  width: "100% !important",
                  height: "auto !important",
                  maxWidth: 320,
                },
                "& .rccs__card": {
                  width: "auto !important",
                },
                marginBottom: 2
              }}
            >
              <CreditCard
                name={creditCard.name}
                acceptedCards={["mastercard", "visa", "amex", "elo", "hipercard", "hiper"]}
                number={creditCard.number}
                cvc={creditCard.cvc}
                expiry={creditCard.expiry}
                focused={focusedField}
                placeholders={{ name: "SEU NOME" }}
                callback={onIdentifyCreditCard}
              />
            </Box>

            <TextField
              variant="outlined"
              label="Nome no Cartão"
              fullWidth
              required
              value={creditCard.name}
              onChange={onCCNameChange}
              onFocus={() => onCCFocusChange("name")}
              error={creditCardErrors.name}
              autoComplete="cc-name"
            />

            <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2}>
              <TextField
                variant="outlined"
                label="Número do Cartão"
                required
                fullWidth
                error={creditCardErrors.number}
                inputProps={{ maxLength: 16 }}
                value={creditCard.number}
                onChange={onCCNumberChange}
                onFocus={() => onCCFocusChange("number")}
                sx={{ flex: 4 }}
                autoComplete="off"
              />

              <TextField
                variant="outlined"
                required
                label="Expira em"
                fullWidth
                value={maskExpiry(creditCard.expiry)}
                error={creditCardErrors.expiry}
                onChange={onCCExpiryChange}
                onFocus={() => onCCFocusChange("expiry")}
                placeholder="MM/AA"
                sx={{ flex: 1 }}
                autoComplete="cc-exp"
              />

              <TextField
                variant="outlined"
                label="CVV"
                required
                fullWidth
                value={creditCard.cvc}
                error={creditCardErrors.cvc}
                onChange={onCCCVCChange}
                onFocus={() => onCCFocusChange("cvc")}
                sx={{ flex: 1 }}
                autoComplete="off"
              />
            </Box>
          </Box>

          {renderInstallments()}
        </>
      )}
    </Box>
  );
};

export default PaymentMethodForm;
